/**
 * TypeScript types for the in-app notifications system
 */

/**
 * Notification types matching backend NotificationType enum
 */
export const NotificationType = {
  // Ticket-related notifications
  TicketAssigned: 1,
  TicketComment: 2,
  TicketStatusChanged: 3,
  TicketCreated: 4,
  ParticipantAdded: 5,
  TicketMerged: 6,
  FollowerAdded: 7,
  TicketUpdated: 9,

  // Reminder notifications
  TicketReminderInProgress: 10,
  TicketReminderPending: 11,
  TicketReminderOverdue: 12,
  TicketReminderOpen: 13,
  TicketReminder: 16,

  // Account notifications
  PasswordChanged: 20,
  AccountLocked: 21,
  WelcomeMessage: 22,
  OrganizationInvite: 23,

  // System notifications
  SystemAnnouncement: 30,
  FeatureUpdate: 31,
  Info: 32,
} as const;

export type NotificationType = (typeof NotificationType)[keyof typeof NotificationType];

/**
 * Individual notification response
 */
export interface NotificationResponse {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  actionUrl: string | null;
  entityType: string | null;
  entityId: string | null;
  metadata: string | null;
  isRead: boolean;
  readAt: string | null;
  createdAt: string;
}

/**
 * Notification list response with pagination
 * Note: Property names match backend C# DTOs (PascalCase converted to camelCase by JSON serialization)
 */
export interface NotificationListResponse {
  notifications: NotificationResponse[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  pageCount: number;
  unreadCount: number;
}

/**
 * Notification count response
 */
export interface NotificationCountResponse {
  unreadCount: number;
}

/**
 * Filter request for fetching notifications
 * Note: Property names match backend C# DTOs
 */
export interface NotificationFilterRequest {
  page?: number;
  pageSize?: number;
  isRead?: boolean | null;
  type?: NotificationType | null;
  entityType?: string | null;
  fromDate?: string | null;
  toDate?: string | null;
}

/**
 * Request to mark multiple notifications as read
 */
export interface MarkNotificationsReadRequest {
  notificationIds: string[];
}

/**
 * Get a user-friendly label for a notification type
 */
export function getNotificationTypeLabel(type: NotificationType): string {
  switch (type) {
    case NotificationType.TicketAssigned:
      return "Ticket Assigned";
    case NotificationType.TicketComment:
      return "New Comment";
    case NotificationType.TicketStatusChanged:
      return "Status Changed";
    case NotificationType.TicketCreated:
      return "New Ticket";
    case NotificationType.ParticipantAdded:
      return "Participant Added";
    case NotificationType.TicketMerged:
      return "Ticket Merged";
    case NotificationType.FollowerAdded:
      return "Following Ticket";
    case NotificationType.TicketUpdated:
      return "Ticket Updated";
    case NotificationType.TicketReminderInProgress:
      return "In Progress Reminder";
    case NotificationType.TicketReminderPending:
      return "Pending Reminder";
    case NotificationType.TicketReminderOverdue:
      return "Overdue";
    case NotificationType.TicketReminderOpen:
      return "Open Ticket Reminder";
    case NotificationType.TicketReminder:
      return "Ticket Reminder";
    case NotificationType.PasswordChanged:
      return "Password Changed";
    case NotificationType.AccountLocked:
      return "Account Locked";
    case NotificationType.WelcomeMessage:
      return "Welcome";
    case NotificationType.OrganizationInvite:
      return "Organization Invite";
    case NotificationType.SystemAnnouncement:
      return "Announcement";
    case NotificationType.FeatureUpdate:
      return "Feature Update";
    case NotificationType.Info:
      return "Information";
    default:
      return "Notification";
  }
}

/**
 * Check if a notification type is a reminder type
 */
export function isReminderNotification(type: NotificationType): boolean {
  const reminderTypes: NotificationType[] = [
    NotificationType.TicketReminderInProgress,
    NotificationType.TicketReminderPending,
    NotificationType.TicketReminderOverdue,
    NotificationType.TicketReminderOpen,
    NotificationType.TicketReminder,
  ];
  return reminderTypes.includes(type);
}

/**
 * Check if a notification type is a ticket-related type
 */
export function isTicketNotification(type: NotificationType): boolean {
  const ticketTypes: NotificationType[] = [
    NotificationType.TicketAssigned,
    NotificationType.TicketComment,
    NotificationType.TicketStatusChanged,
    NotificationType.TicketCreated,
    NotificationType.ParticipantAdded,
    NotificationType.FollowerAdded,
    NotificationType.TicketUpdated,
    NotificationType.TicketMerged,
  ];
  return ticketTypes.includes(type);
}

/**
 * Check if a notification type is a system/account type
 */
export function isSystemNotification(type: NotificationType): boolean {
  const systemTypes: NotificationType[] = [
    NotificationType.PasswordChanged,
    NotificationType.AccountLocked,
    NotificationType.WelcomeMessage,
    NotificationType.OrganizationInvite,
    NotificationType.SystemAnnouncement,
    NotificationType.FeatureUpdate,
    NotificationType.Info,
  ];
  return systemTypes.includes(type);
}
